
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { formatDateForDisplay } from '../../utils/dateUtils';

interface DatePickerProps {
  label?: string;
  value: string; // YYYY-MM-DD
  onChange: (date: string) => void;
  placeholder?: string;
  minDate?: string;
}

export const DatePicker: React.FC<DatePickerProps> = ({ label, value, onChange, placeholder = "Select date", minDate }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [viewDate, setViewDate] = useState(new Date());

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.overflow = 'hidden';

      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  // Initialize viewDate based on value or today
  useEffect(() => {
    if (value) {
      const [y, m, d] = value.split('-').map(Number);
      setViewDate(new Date(y, m - 1, d));
    }
  }, [value, isOpen]);

  const handleDayClick = (day: number, monthOffset: number = 0) => {
    // Clone viewDate to avoid mutation issues
    const targetDate = new Date(viewDate.getFullYear(), viewDate.getMonth() + monthOffset, day);

    const year = targetDate.getFullYear();
    const month = targetDate.getMonth();
    const d = targetDate.getDate();

    // Format YYYY-MM-DD
    const dateString = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

    onChange(dateString);
    setIsOpen(false);
  };

  const changeMonth = (delta: number) => {
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + delta, 1));
  };

  // Calendar Logic
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();

  const firstDayOfMonth = new Date(year, month, 1).getDay(); // 0 = Sunday
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrevMonth = new Date(year, month, 0).getDate();

  const days = [];

  // Previous Month Padding
  for (let i = firstDayOfMonth - 1; i >= 0; i--) {
    const day = daysInPrevMonth - i;
    days.push({ day, offset: -1, key: `prev-${day}` });
  }

  // Current Month
  for (let i = 1; i <= daysInMonth; i++) {
    days.push({ day: i, offset: 0, key: `curr-${i}` });
  }

  // Next Month Padding (fill up to 42 cells for 6 rows)
  const remainingCells = 42 - days.length;
  for (let i = 1; i <= remainingCells; i++) {
    days.push({ day: i, offset: 1, key: `next-${i}` });
  }

  const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

  return (
    <div className="w-full">
      {label && <label className="block text-xs font-bold text-stone-400 uppercase mb-1 ml-1">{label}</label>}

      {/* Input Trigger */}
      <button
        onClick={(e) => { e.preventDefault(); setIsOpen(true); }}
        className={`w-full px-4 py-3 rounded-xl bg-stone-50 border-2 text-left flex items-center justify-between transition-all outline-none group
          ${isOpen ? 'border-brand-500 ring-4 ring-brand-500/10 bg-white' : 'border-stone-100 hover:border-brand-200 hover:bg-white'}
        `}
      >
        <span className={value ? "text-stone-900 font-bold font-mono tracking-tight" : "text-stone-400 font-medium"}>
          {value ? formatDateForDisplay(value) : placeholder}
        </span>
        <div className={`p-2 rounded-lg transition-colors ${isOpen || value ? 'bg-brand-50 text-brand-600' : 'bg-stone-100 text-stone-400 group-hover:text-stone-600'}`}>
          <CalendarIcon className="w-5 h-5" />
        </div>
      </button>

      {/* Modal - rendered via portal to document.body to bypass parent scroll containers */}
      {isOpen && createPortal(
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-stone-900/60 backdrop-blur-sm animate-fade-in"
          onClick={() => setIsOpen(false)}
        >
          <div
            className="bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-6 pb-2">
              <div className="flex items-center justify-between mb-6">
                <button
                  onClick={() => changeMonth(-1)}
                  className="p-3 hover:bg-stone-100 rounded-2xl text-stone-500 transition-colors active:scale-95"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <div className="text-center">
                  <h3 className="text-xl font-bold text-stone-900 leading-none">
                    {monthNames[month]}
                  </h3>
                  <p className="text-stone-400 font-medium text-sm mt-1">{year}</p>
                </div>
                <button
                  onClick={() => changeMonth(1)}
                  className="p-3 hover:bg-stone-100 rounded-2xl text-stone-500 transition-colors active:scale-95"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              </div>

              {/* Weekday Labels */}
              <div className="grid grid-cols-7 mb-2">
                {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                  <div key={day} className="h-10 flex items-center justify-center text-[11px] font-bold text-stone-500 uppercase tracking-wider">
                    {day}
                  </div>
                ))}
              </div>
            </div>

            {/* Days Grid - Fixed Height Container */}
            <div className="px-6 pb-6">
              <div className="grid grid-cols-7 gap-1">
                {days.map(({ day, offset, key }) => {
                  // Logic to determine selection state and disabled state
                  const cellDate = new Date(year, month + offset, day);
                  const cellYear = cellDate.getFullYear();
                  const cellMonth = cellDate.getMonth();

                  // Format for comparison (YYYY-MM-DD)
                  // Note: Use manual construction to ensure local time match logic
                  const cellDateString = `${cellYear}-${String(cellMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

                  const isSelected = value === cellDateString;

                  const isToday =
                    new Date().getDate() === day &&
                    new Date().getMonth() === cellMonth &&
                    new Date().getFullYear() === cellYear;

                  const isDisabled = minDate && cellDateString < minDate;

                  return (
                    <button
                      key={key}
                      onClick={(e) => { e.preventDefault(); handleDayClick(day, offset); }}
                      disabled={!!isDisabled}
                      className={`
                                h-10 w-full rounded-xl flex items-center justify-center text-sm font-medium transition-all duration-200
                                ${isDisabled
                          ? 'text-stone-200 cursor-not-allowed bg-transparent'
                          : offset === 0 ? 'text-stone-700' : 'text-stone-300'
                        }
                                ${!isDisabled && isSelected
                          ? 'bg-brand-600 text-white shadow-lg shadow-brand-500/30 scale-110 z-10 font-bold'
                          : !isDisabled && 'hover:bg-stone-100 active:scale-95'
                        }
                                ${!isDisabled && !isSelected && isToday ? 'bg-brand-50 text-brand-600 font-bold border border-brand-100' : ''}
                            `}
                    >
                      {day}
                    </button>
                  );
                })}
              </div>

              {/* Actions */}
              <div className="mt-6 pt-4 border-t border-stone-100 flex justify-between items-center">
                <button
                  onClick={() => {
                    const today = new Date();
                    const y = today.getFullYear();
                    const m = String(today.getMonth() + 1).padStart(2, '0');
                    const d = String(today.getDate()).padStart(2, '0');
                    const todayString = `${y}-${m}-${d}`;

                    // Only allow selecting today if it's not disabled
                    if (!minDate || todayString >= minDate) {
                      onChange(todayString);
                      setIsOpen(false);
                    }
                  }}
                  className={`text-xs font-bold uppercase tracking-wide px-2 py-1 ${minDate && new Date().toISOString().split('T')[0] < minDate
                    ? 'text-stone-300 cursor-not-allowed'
                    : 'text-brand-600 hover:text-brand-700'
                    }`}
                  disabled={!!(minDate && new Date().toISOString().split('T')[0] < minDate)}
                >
                  Today
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  className="px-6 py-2.5 rounded-xl bg-stone-100 text-stone-600 font-bold text-sm hover:bg-stone-200 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};
